import { Link } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import useSWR from 'swr';
import { AuthProviderSkeleton } from '~/components/auth-provider-skeleton';
import { FormField } from '~/components/form-field';
import { PhoneInput } from '~/components/phone-input';
import type { OriginName } from '~/interfaces/common';
import { m } from '~/paraglide/messages.js';
import { getAllAvailableOrigins } from '../-api/api';
import { useSignupForm } from '../-hooks/use-signup-form';
import { getOrigins } from './auth-aroviders';
import { AuthProviderButton } from './auth-provider-button';

type FieldName = 'name' | 'lastName' | 'email' | 'phone' | 'usage';

const SIGNUP_FORM_KEY = 'signup_form_draft';

function getPersistedForm(initialEmail?: string, initialPhone?: string) {
  try {
    const saved = sessionStorage.getItem(SIGNUP_FORM_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        name: parsed.name ?? '',
        lastName: parsed.lastName ?? '',
        email: parsed.email || initialEmail || '',
        phone: parsed.phone || initialPhone || '',
        usage: parsed.usage ?? 'business',
      };
    }
  } catch {
    // ignore parse errors
  }
  return {
    name: '',
    lastName: '',
    email: initialEmail ?? '',
    phone: initialPhone ?? '',
    usage: 'business',
  };
}

interface AccountProfileProps {
  initialEmail?: string;
  initialPhone?: string;
}

export function AccountProfileComponent({
  initialEmail,
  initialPhone,
}: AccountProfileProps) {
  const { createAccount } = useSignupForm();
  const { data, isLoading } = useSWR('/origins', getAllAvailableOrigins);

  const [form, setForm] = useState(() =>
    getPersistedForm(initialEmail, initialPhone),
  );
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const persistForm = useCallback((updated: typeof form) => {
    try {
      sessionStorage.setItem(SIGNUP_FORM_KEY, JSON.stringify(updated));
    } catch {
      // ignore quota errors
    }
  }, []);

  const handleSubmit = async (namespace: OriginName, provider: string) => {
    if (isSubmitting) return;

    const newErrors: Partial<Record<FieldName, string>> = {};

    if (!form.name.trim())
      newErrors.name = m['auth.validation.name_required']();
    if (!form.lastName.trim())
      newErrors.lastName = m['auth.validation.lastname_required']();
    if (!form.email.trim()) {
      newErrors.email = m['auth.validation.email_required']();
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = m['auth.validation.email_invalid']();
    }

    // Validate phone: should have country code (1-3 digits) + phone number (7-15 digits)
    if (
      !form.phone ||
      typeof form.phone !== 'string' ||
      form.phone.trim().length === 0
    ) {
      newErrors.phone = m['auth.validation.phone_required']();
    } else {
      // Phone should have at least country code + 7 digits
      if (form.phone.length < 8) {
        newErrors.phone = m['auth.validation.phone_required']();
      } else {
        // Extract phone number without country code for validation
        const phoneNumber = form.phone.replace(/^\d{1,3}/, ''); // Remove 1-3 digit country code
        if (!phoneNumber.trim() || phoneNumber.trim().length < 7) {
          newErrors.phone = m['auth.validation.phone_required']();
        } else if (!/^\d{7,15}$/.test(phoneNumber)) {
          newErrors.phone = m['auth.validation.phone_invalid']();
        }
      }
    }

    if (!form.usage.trim())
      newErrors.usage = m['auth.validation.usage_required']();

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        setIsSubmitting(true);
        sessionStorage.removeItem(SIGNUP_FORM_KEY);
        await createAccount(namespace, provider, form);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;

    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      persistForm(updated);
      return updated;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      Reflect.deleteProperty(newErrors, name);
      return newErrors;
    });
  };

  return (
    <>
      <form className="space-y-4 border rounded-xl p-8">
        <FormField
          label={m['auth.signup.name_label']()}
          name="name"
          placeholder={m['auth.signup.name_placeholder']()}
          value={form.name}
          onInput={handleChange}
          error={errors.name}
        />

        <FormField
          label={m['auth.signup.lastname_label']()}
          name="lastName"
          placeholder={m['auth.signup.lastname_placeholder']()}
          value={form.lastName}
          onInput={handleChange}
          error={errors.lastName}
        />

        <FormField
          label={m['auth.signup.email_label']()}
          name="email"
          placeholder={m['auth.signup.email_placeholder']()}
          type="email"
          value={form.email}
          onInput={handleChange}
          error={errors.email}
        />

        <div className="space-y-2">
          <label
            htmlFor="phone"
            className="text-sm font-medium text-primary-700"
          >
            {m['auth.signup.phone_label']()}
            <span className="text-red-500">*</span>
          </label>

          <PhoneInput
            name="phone"
            value={form.phone}
            onChange={(phoneValue) => {
              setForm((prev) => {
                const updated = { ...prev, phone: phoneValue };
                persistForm(updated);
                return updated;
              });
              setErrors((prev) => {
                const newErrors = { ...prev };
                Reflect.deleteProperty(newErrors, 'phone');
                return newErrors;
              });
            }}
            hasError={!!errors.phone}
            required={true}
            placeholder={m['auth.signup.phone_placeholder']()}
          />

          {errors.phone && (
            <p className="text-destructive font-normal text-sm">
              {errors.phone}
            </p>
          )}
        </div>

        {/* <div className="space-y-2">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: label is associated with Select component via htmlFor *
          <label className="text-sm font-medium text-primary-700">
            ¿Para qué usarás Bloque?<span className="text-red-500">*</span>
          </label>

          <div className="flex space-x-2">
            <Select
              name="usage"
              onValueChange={(value) =>
                // biome-ignore lint/suspicious/noExplicitAny: synthetic event needed for consistent form handling
                handleChange({ currentTarget: { name: 'usage', value } } as any)
              }
              defaultValue={form.usage}
            >
              <SelectTrigger
                className={cn('w-full', {
                  'border-red-500 ring-1 ring-red-500': !!errors.usage,
                })}
              >
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Uso personal</SelectItem>
                <SelectItem value="business">Negocio</SelectItem>
                <SelectItem value="freelance">Freelance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {errors.usage && (
            <p className="text-destructive font-normal text-sm">
              {errors.usage}
            </p>
          )}
        </div> */}

        <div className="space-y-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-primary-foreground text-gray-500 dark:text-gray-300">
                {m['auth.signup.continue_with']()}
              </span>
            </div>
          </div>

          {isLoading ? (
            <AuthProviderSkeleton />
          ) : (
            <div className="flex flex-wrap gap-3 justify-center">
              {data?.map((origin) => {
                const ProviderIcons = getOrigins()[origin.namespace];
                if (!ProviderIcons) return null;

                const handleClick =
                  (providerId: string) =>
                  (e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleSubmit(origin.namespace, providerId);
                  };

                if (!Array.isArray(ProviderIcons)) {
                  return (
                    <AuthProviderButton
                      Icon={ProviderIcons.icon}
                      onClick={handleClick(ProviderIcons.id)}
                      disabled={isSubmitting}
                    />
                  );
                }

                return ProviderIcons.map((provider) => {
                  return (
                    <AuthProviderButton
                      id={provider.id}
                      key={provider.id}
                      Icon={provider.icon}
                      onClick={handleClick(provider.id)}
                      disabled={isSubmitting}
                    />
                  );
                });
              })}
            </div>
          )}
        </div>

        <div className="text-center">
          <p className="text-sm text-primary-600">
            {m['auth.signup.have_account']()}{' '}
            <Link
              to="/auth/login"
              className="text-primary-900 hover:text-primary-700 underline font-medium"
            >
              {m['auth.signup.login_link']()}
            </Link>
          </p>
        </div>
      </form>

      <div className="text-center md:mx-18 mx-6">
        <p className="text-xs text-gray-500 dark:text-gray-300">
          {m['auth.signup.terms_text']()}{' '}
          <button type="button" className="underline hover:text-gray-700">
            {m['auth.signup.terms_link']()}
          </button>{' '}
          {m['auth.signup.terms_and']()}{' '}
          <button type="button" className="underline hover:text-gray-700">
            {m['auth.signup.privacy_link']()}
          </button>
        </p>
      </div>
    </>
  );
}
